import { useTranslation } from 'react-i18next';
import { Skeleton } from '@monksflow/monks-ui';
import { EM_DASH, formatMetric } from '@/shared/utils/metric-format';
import type { DeliveryTotals } from '../../lib/metrics';
import { buildFunnel } from '../../lib/funnel';

export interface ConversionFunnelProps {
  totals: DeliveryTotals;
  isLoading?: boolean;
}

/**
 * Fixed slots, not seriesVarForPlatform: these three bars are pipeline stages, not
 * platforms, so a lookup keyed by platform identity has nothing to key on. --series-3
 * falls below the 3:1 contrast floor in light mode (see global.css), which is exactly why
 * every bar below also carries its own visible label rather than relying on colour alone
 * to identify it.
 */
const STEP_COLORS = ['var(--series-1)', 'var(--series-2)', 'var(--series-3)'];

/**
 * The rate name and the empty-step reason for each non-first step, held as keys so the
 * table stays a module constant without freezing a translation at import.
 *
 * Each reason names the step that is actually empty, not the step showing the dash: index 1's
 * rate is impressions -> clicks, and index 2's is clicks -> conversions, so swapping either
 * sentence onto the other bar would name the wrong step and stop being true.
 *
 * A reason only applies when the previous step is a measured zero. When the previous step's
 * total is itself unknown (null), saying "No impressions in this period" would assert a
 * measurement that was never made - `unknownTotal` covers that case instead: it names the
 * totals themselves being unavailable (e.g. a settled query with no data).
 */
const STEP_RATE_KEYS = {
  1: {
    rateNameKey: 'performanceDashboard.conversionFunnel.clickThroughRate',
    emptyReasonKey: 'performanceDashboard.conversionFunnel.noImpressionsInThisPeriod',
  },
  2: {
    rateNameKey: 'performanceDashboard.conversionFunnel.conversionRate',
    emptyReasonKey: 'performanceDashboard.conversionFunnel.noClicksInThisPeriod',
  },
} as const;

type RateStepIndex = keyof typeof STEP_RATE_KEYS;

/**
 * Impression -> click -> conversion as horizontal bars, widest at the top.
 */
export function ConversionFunnel({ totals, isLoading = false }: ConversionFunnelProps) {
  const { t } = useTranslation('projects');

  if (isLoading) {
    return <Skeleton className="h-40 w-full" data-testid="conversion-funnel-skeleton" />;
  }

  const steps = buildFunnel(totals);
  // An unknown step contributes no bar rather than distorting the others' scale, the
  // same way it contributes no "0" to the printed value below.
  const maxValue = Math.max(0, ...steps.map((step) => step.value ?? 0));

  return (
    <div className="flex flex-col gap-3" data-testid="conversion-funnel">
      {steps.map((step, index) => {
        const widthPercent =
          maxValue > 0 && step.value !== null ? (step.value / maxValue) * 100 : 0;
        const rateKeys = STEP_RATE_KEYS[index as RateStepIndex];
        const unknownTotalReason = t('performanceDashboard.conversionFunnel.unknownTotal');

        return (
          <div key={step.label} className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span className="font-medium text-foreground">{step.label}</span>
              <div className="flex items-baseline gap-2 text-muted-foreground">
                <span data-testid={`conversion-funnel-value-${index}`}>
                  {step.value === null
                    ? `${EM_DASH} ${unknownTotalReason}`
                    : formatMetric(step.value, 'integer')}
                </span>
                {index > 0 && (
                  <span data-testid={`conversion-funnel-rate-${index}`}>
                    {step.rateFromPrevious === null
                      ? `${EM_DASH} ${
                          steps[index - 1].value === null
                            ? unknownTotalReason
                            : t(rateKeys.emptyReasonKey)
                        }`
                      : `${t(rateKeys.rateNameKey)} ${formatMetric(step.rateFromPrevious, 'percent')}`}
                  </span>
                )}
              </div>
            </div>
            <div
              className="h-8 rounded-sm"
              style={{ width: `${widthPercent}%`, backgroundColor: STEP_COLORS[index] }}
              data-testid={`conversion-funnel-bar-${index}`}
            />
          </div>
        );
      })}
    </div>
  );
}
